import React, { useState, useEffect } from 'react';
import { Trash2, RotateCcw, AlertTriangle, Clock, User, Calendar, CheckCircle, XCircle, Shield, Eye, RefreshCw } from 'lucide-react';
import api from '../utils/api';


const RecoverySettings = ({ user }) => {
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [confirmAction, setConfirmAction] = useState({ show: false, type: '', items: [] });

  useEffect(() => {
    fetchDeletedItems();
  }, []);

  const fetchDeletedItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/recovery/deleted-items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Convert response to items array format
      const items = [];
      if (response.data.clients) {
        response.data.clients.forEach(c => items.push({ ...c, type: 'client' }));
      }
      if (response.data.projects) {
        response.data.projects.forEach(p => items.push({ ...p, type: 'project' }));
      }
      if (response.data.tasks) {
        response.data.tasks.forEach(t => items.push({ ...t, type: 'task' }));
      }
      
      setDeletedItems(items);
    } catch (error) {
      console.error('Failed to fetch deleted items:', error);
      if (error.response?.status === 403) {
        showNotification('error', 'Access denied. Only administrators can access recovery features.');
      } else {
        showNotification('error', 'Failed to load deleted items');
      }
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  const handleRestore = async (item) => {
    setConfirmAction({
      show: true,
      type: 'restore',
      items: [item]
    });
  };

  const handlePermanentDelete = async (item) => {
    setConfirmAction({
      show: true,
      type: 'permanent_delete',
      items: [item]
    });
  };

  const handleBulkRestore = async () => {
    const items = deletedItems.filter(item => selectedItems.has(item.id));
    setConfirmAction({
      show: true,
      type: 'bulk_restore',
      items: items
    });
  };

  const executeAction = async () => {
    setProcessing(true);
    const { type, items } = confirmAction;
    
    try {
      const token = localStorage.getItem('access_token');
      let successCount = 0;
      let errorCount = 0;

      for (const item of items) {
        try {
          if (type === 'restore' || type === 'bulk_restore') {
            // Use pluralized type for API (client -> clients, project -> projects, task -> tasks)
            const itemType = item.type + 's';
            await api.post(`/recovery/restore/${itemType}/${item.id}`, {}, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            successCount++;
          } else if (type === 'permanent_delete') {
            showNotification('error', 'Permanent delete not implemented. Items remain soft deleted.');
            errorCount++;
          }
        } catch (error) {
          console.error(`Failed to ${type} item ${item.id}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        const actionName = type.includes('restore') ? 'restored' : 'permanently deleted';
        showNotification('success', `${successCount} items ${actionName} successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
        await fetchDeletedItems();
        setSelectedItems(new Set());
      } else {
        showNotification('error', 'Failed to process selected items');
      }

    } catch (error) {
      console.error('Action failed:', error);
      showNotification('error', 'Operation failed');
    } finally {
      setProcessing(false);
      setConfirmAction({ show: false, type: '', items: [] });
    }
  };

  const handleManualCleanup = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post(`/admin/recovery/cleanup-expired`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      showNotification('success', response.data.message);
      await fetchDeletedItems(); // Refresh the list
    } catch (error) {
      console.error('Manual cleanup failed:', error);
      if (error.response?.status === 403) {
        showNotification('error', 'Access denied. Only administrators can trigger cleanup.');
      } else {
        showNotification('error', 'Cleanup operation failed');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatTimeLeft = (expiresString) => {
    const expires = new Date(expiresString);
    const now = new Date();
    const timeLeft = expires - now;
    const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) return 'Expired';
    if (daysLeft === 1) return '1 day left';
    return `${daysLeft} days left`;
  };

  const getItemIcon = (itemType) => {
    switch (itemType) {
      case 'client': return <User className="w-5 h-5 text-blue-600" />;
      case 'project': return <Calendar className="w-5 h-5 text-green-600" />;
      case 'task': return <CheckCircle className="w-5 h-5 text-purple-600" />;
      default: return <Eye className="w-5 h-5 text-gray-600" />;
    }
  };

  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
        <div className="space-y-4">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800'
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Recovery Center</h3>
            <p className="text-sm text-gray-600">
              Restore deleted items within 10 days or permanently delete them.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {selectedItems.size > 0 && (
              <button
                onClick={handleBulkRestore}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Restore Selected ({selectedItems.size})</span>
              </button>
            )}
            <button
              onClick={fetchDeletedItems}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleManualCleanup}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span>Cleanup Expired</span>
            </button>
          </div>
        </div>

        {/* Deleted Items List */}
        {deletedItems.length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  checked={selectedItems.size === deletedItems.length && deletedItems.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedItems(new Set(deletedItems.map(item => item.id)));
                    } else {
                      setSelectedItems(new Set());
                    }
                  }}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  {deletedItems.length} deleted items • {selectedItems.size} selected
                </span>
              </div>
            </div>

            <div className="divide-y divide-gray-200">
              {deletedItems.map((item) => (
                <div key={item.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => toggleItemSelection(item.id)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div className="flex items-center space-x-3">
                        {getItemIcon(item.type)}
                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-gray-900">{item.item_name}</h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              item.type === 'client' ? 'bg-blue-100 text-blue-800' :
                              item.type === 'project' ? 'bg-green-100 text-green-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {item.type}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                            <span>Deleted: {formatDate(item.deleted_at)}</span>
                            {item.deleted_by_name && (
                              <span>By: {item.deleted_by_name}</span>
                            )}
                            <span className={`font-medium ${
                              formatTimeLeft(item.recovery_expires).includes('day') ? 'text-orange-600' : 'text-red-600'
                            }`}>
                              {formatTimeLeft(item.recovery_expires)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRestore(item)}
                        className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors text-sm"
                      >
                        <RotateCcw className="w-4 h-4" />
                        <span>Restore</span>
                      </button>
                      <button
                        onClick={() => handlePermanentDelete(item)}
                        className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Permanent Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Deleted Items</h3>
            <p className="text-gray-600">
              All items are safe! There are no deleted items in the recovery system.
            </p>
          </div>
        )}

        {/* Information Panel */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <h4 className="font-medium text-blue-900 mb-1">Recovery System Information</h4>
              <ul className="text-blue-800 space-y-1">
                <li>• Deleted items are kept for <strong>10 days</strong> before permanent deletion</li>
                <li>• Restoring a client also restores all associated projects and tasks</li>
                <li>• General Projects cannot be deleted individually - only via client deletion</li>
                <li>• Only administrators can access recovery features</li>
                <li>• Automatic cleanup runs daily at 2 AM</li>
                <li>• Manual cleanup can be triggered using the "Cleanup Expired" button</li>
                <li>• Permanent deletion cannot be undone</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmAction.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  confirmAction.type.includes('restore') ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {confirmAction.type.includes('restore') ? (
                    <RotateCcw className="w-6 h-6 text-green-600" />
                  ) : (
                    <Trash2 className="w-6 h-6 text-red-600" />
                  )}
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {confirmAction.type.includes('restore') ? 'Confirm Restore' : 'Confirm Permanent Deletion'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {confirmAction.type.includes('restore') ? 'This will restore the item(s)' : 'This cannot be undone'}
                  </p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-gray-700 mb-3">
                  {confirmAction.type.includes('restore') 
                    ? `Are you sure you want to restore ${confirmAction.items.length} item(s)?`
                    : `Are you sure you want to permanently delete ${confirmAction.items.length} item(s)?`
                  }
                </p>
                
                <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                  {confirmAction.items.map((item) => (
                    <div key={item.id} className="flex items-center space-x-2 text-sm text-gray-700">
                      {getItemIcon(item.type)}
                      <span className="font-medium">{item.item_name}</span>
                      <span className="text-gray-500">({item.type})</span>
                    </div>
                  ))}
                </div>

                {!confirmAction.type.includes('restore') && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                    <p className="text-sm text-red-800">
                      <strong>Warning:</strong> Permanent deletion cannot be undone. These items will be completely removed from the system.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmAction({ show: false, type: '', items: [] })}
                  disabled={processing}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={executeAction}
                  disabled={processing}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                    confirmAction.type.includes('restore')
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {processing && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  <span>
                    {processing ? 'Processing...' : 
                     confirmAction.type.includes('restore') ? 'Restore Items' : 'Permanently Delete'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RecoverySettings;